import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../utils/api';
import { parseUTC } from '../utils/formatters';

type TabId = 'overview' | 'cost' | 'browse';

interface DatasetsStats {
  total_files: number;
  total_size_bytes: number;
  total_size_human: string;
  bucket: string;
  prefix: string;
}

interface ReplicationRun {
  id: number;
  trigger: string;
  status: string;
  dataset_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  files_scanned: number;
  files_transferred: number;
  files_skipped: number;
  files_failed: number;
  bytes_transferred: number;
  duration_seconds: number | null;
  avg_throughput_mbps: number | null;
  chunk_size_bytes: number | null;
  egress_cost: number;
  storage_cost_delta: number;
  api_cost: number;
  total_cost: number;
  error_message: string | null;
}

interface CostSummary {
  period_days: number;
  total_runs: number;
  total_bytes_transferred: number;
  total_egress_cost: number;
  total_api_cost: number;
  total_storage_cost: number;
  total_cost: number;
  avg_throughput_mbps: number;
  avg_duration_seconds: number;
  cost_trend: { date: string; cost: number; bytes: number }[];
}

interface DatasetFile {
  name: string;
  size_mb: number;
}

interface DatasetEntry {
  id: string;
  exchange: string;
  category: string;
  description: string;
  timeframes: string[];
  pair_count: number;
  total_size_mb: number;
  last_refresh: string | null;
  files: Record<string, DatasetFile>;
  mount_path: string;
}

interface BrowseItem {
  name: string;
  path: string;
  type: 'directory' | 'file';
  size?: number;
  last_modified?: string;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '-';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

function formatCost(cost: number): string {
  if (cost === 0) return '$0.00';
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(2)}`;
}

/** Static pipeline config derived from category/exchange — no API call needed */
function getPipelineConfig(category: string, exchange: string) {
  const cat = category.toLowerCase();
  if (cat === 'crypto') {
    return {
      source: `${exchange.charAt(0).toUpperCase() + exchange.slice(1)} REST API`,
      refreshScript: 'refresh_crypto_data.py',
      refreshSchedule: 'Manual (cron TBD)',
      replicationSchedule: 'Event-driven on refresh + 24h safety net',
      s3Prefix: `datasets/crypto/${exchange}/consolidated/`,
      delivery: {
        aws: 'S3 mount-s3 FUSE',
        gcp: 'S3 \u2192 GCS \u2192 gcsfuse',
      },
    };
  }
  return {
    source: 'Unknown',
    refreshScript: 'refresh_data.py',
    refreshSchedule: 'Manual (cron TBD)',
    replicationSchedule: 'Event-driven on refresh + 24h safety net',
    s3Prefix: `datasets/${cat}/${exchange}/`,
    delivery: { aws: 'S3 mount-s3 FUSE', gcp: 'S3 \u2192 GCS \u2192 gcsfuse' },
  };
}

function getSyncStatus(lastRun: ReplicationRun | null): { label: string; color: string; dotColor: string } {
  if (!lastRun) return { label: 'No data', color: 'text-gray-500', dotColor: 'bg-gray-600' };
  if (lastRun.status === 'failed') return { label: 'Failed', color: 'text-red-400', dotColor: 'bg-red-500' };
  if (lastRun.status === 'in_progress') return { label: 'Syncing', color: 'text-yellow-400', dotColor: 'bg-yellow-500' };
  if (lastRun.status === 'completed' && lastRun.files_transferred === 0) {
    return { label: 'Cached', color: 'text-green-400', dotColor: 'bg-green-500' };
  }
  return { label: 'Synced', color: 'text-green-400', dotColor: 'bg-green-500' };
}

export default function AdminDatasetsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  // Overview state
  const [stats, setStats] = useState<DatasetsStats | null>(null);
  const [datasets, setDatasets] = useState<DatasetEntry[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [lastRuns, setLastRuns] = useState<Record<string, ReplicationRun | null>>({});
  const [expandedPipelines, setExpandedPipelines] = useState<Set<string>>(new Set());

  // Cost state
  const [costSummary, setCostSummary] = useState<CostSummary | null>(null);
  const [costLoading, setCostLoading] = useState(false);
  const [runs, setRuns] = useState<ReplicationRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);

  // Browse state
  const [browsePath, setBrowsePath] = useState('');
  const [browseItems, setBrowseItems] = useState<BrowseItem[]>([]);
  const [browseLoading, setBrowseLoading] = useState(false);

  const headers = { 'Authorization': `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchStats();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'cost') {
      fetchCostData();
    } else if (activeTab === 'browse') {
      fetchBrowse(browsePath);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [statsResp, catalogResp, runsResp] = await Promise.all([
        fetch(`${getApiUrl()}/api/v1/admin/datasets/stats`, { headers }),
        fetch(`${getApiUrl()}/api/v1/datasets/catalog`, { headers }),
        fetch(`${getApiUrl()}/api/v1/admin/datasets/replication-history?limit=10`, { headers }),
      ]);
      if (statsResp.status === 403) { navigate('/dashboard'); return; }
      if (statsResp.ok) setStats(await statsResp.json());
      if (catalogResp.ok) {
        const catalog = await catalogResp.json();
        setDatasets(catalog.datasets || []);
      }
      if (runsResp.ok) {
        const data = await runsResp.json();
        // Group by dataset_id, take most recent per dataset
        const grouped: Record<string, ReplicationRun> = {};
        for (const run of (data.runs || []) as ReplicationRun[]) {
          const dsId = run.dataset_id || 'all';
          if (!grouped[dsId]) grouped[dsId] = run;
        }
        setLastRuns(grouped);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCostData = useCallback(async () => {
    setCostLoading(true);
    setRunsLoading(true);
    try {
      const [costResp, runsResp] = await Promise.all([
        fetch(`${getApiUrl()}/api/v1/admin/datasets/cost-summary?days=30`, { headers }),
        fetch(`${getApiUrl()}/api/v1/admin/datasets/replication-history?limit=30`, { headers }),
      ]);
      if (costResp.ok) setCostSummary(await costResp.json());
      if (runsResp.ok) {
        const data = await runsResp.json();
        setRuns(data.runs || []);
      }
    } catch (err) {
      console.error('Failed to fetch cost data:', err);
    } finally {
      setCostLoading(false);
      setRunsLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBrowse = useCallback(async (path: string) => {
    setBrowseLoading(true);
    try {
      const resp = await fetch(`${getApiUrl()}/api/v1/admin/datasets/browse?path=${encodeURIComponent(path)}`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        const items: BrowseItem[] = [
          ...(data.directories || []).map((d: any) => ({
            name: d.name || d.prefix?.split('/').filter(Boolean).pop() || d.prefix,
            path: d.prefix || d.path,
            type: 'directory' as const,
          })),
          ...(data.files || []).map((f: any) => ({
            name: f.name,
            path: f.path || f.key,
            type: 'file' as const,
            size: f.size,
            last_modified: f.last_modified,
          })),
        ];
        setBrowseItems(items);
      }
    } catch (err) {
      console.error('Failed to browse:', err);
    } finally {
      setBrowseLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const resp = await fetch(`${getApiUrl()}/api/v1/admin/datasets/replicate`, {
        method: 'POST',
        headers,
      });
      if (resp.ok) {
        const data = await resp.json();
        const processed = data.result?.jobs_processed ?? 0;
        setSyncResult(`Replication complete: ${processed} files processed`);
        fetchStats();
        if (activeTab === 'cost') fetchCostData();
      } else {
        setSyncResult('Replication failed');
      }
    } catch {
      setSyncResult('Replication request failed');
    } finally {
      setSyncing(false);
    }
  };

  /** Find the most recent replication run that covers a given dataset */
  const getLastRunForDataset = (dsId: string): ReplicationRun | null => {
    // Try exact match first, then check runs whose dataset_id contains this dataset
    if (lastRuns[dsId]) return lastRuns[dsId];
    for (const [key, run] of Object.entries(lastRuns)) {
      if (run && key.split(',').some(id => id.trim() === dsId)) return run;
    }
    // Fall back to prefix-wide run
    return lastRuns['all'] || null;
  };

  const togglePipeline = (dsId: string) => {
    setExpandedPipelines(prev => {
      const next = new Set(prev);
      next.has(dsId) ? next.delete(dsId) : next.add(dsId);
      return next;
    });
  };

  const tabs: { id: TabId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'cost', label: 'Cost & Performance' },
    { id: 'browse', label: 'Browse' },
  ];

  // Cost trend bar chart (simple inline SVG)
  const maxCost = costSummary?.cost_trend ? Math.max(...costSummary.cost_trend.map(d => d.cost), 0.001) : 1;

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Datasets</h1>
            <p className="text-sm text-gray-400 mt-1">Manage shared datasets replicated to GPU instances</p>
          </div>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            {syncing ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            {syncing ? 'Replicating...' : 'Trigger Replication'}
          </button>
        </div>

        {syncResult && (
          <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
            syncResult.includes('failed') ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-green-500/10 text-green-400 border border-green-500/20'
          }`}>
            {syncResult}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-900 rounded-lg p-1 border border-gray-800 w-fit">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {statsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 border-2 border-gray-700 border-t-white rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Aggregate stats */}
                {stats && (
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                      <div className="text-2xl font-bold text-white">{datasets.length}</div>
                      <div className="text-xs text-gray-500 mt-1">Datasets</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                      <div className="text-2xl font-bold text-white">{stats.total_size_human}</div>
                      <div className="text-xs text-gray-500 mt-1">Total Size</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                      <div className="text-sm font-mono text-gray-300 mt-1">{stats.bucket}</div>
                      <div className="text-xs text-gray-500 mt-1">S3 Bucket (SSOT)</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                      <div className="flex items-center gap-2 mt-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-sm text-gray-300">GCS Mirror Active</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">propalgos-data-us-central1</div>
                    </div>
                  </div>
                )}

                {/* Per-dataset tiles */}
                {datasets.length > 0 && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {datasets.map(ds => {
                      const category = ds.category.toLowerCase();
                      const dsRunId = `${category}/${ds.exchange}`;
                      const lastRun = getLastRunForDataset(dsRunId);
                      const sync = getSyncStatus(lastRun);
                      const pipeline = getPipelineConfig(category, ds.exchange);
                      const isExpanded = expandedPipelines.has(ds.id);

                      return (
                        <div key={ds.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2.5">
                              <h3 className="text-base font-semibold text-white capitalize">{ds.exchange}</h3>
                              <span className="px-2 py-0.5 text-[10px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-full">
                                {ds.category}
                              </span>
                            </div>
                            {ds.last_refresh && (
                              <span className="text-[11px] text-gray-500">
                                Updated {parseUTC(ds.last_refresh).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>

                          {/* Key stats */}
                          <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="bg-gray-800/50 rounded-lg px-3 py-2">
                              <div className="text-lg font-bold text-white">{ds.pair_count || '\u2014'}</div>
                              <div className="text-[10px] text-gray-500">Pairs</div>
                            </div>
                            <div className="bg-gray-800/50 rounded-lg px-3 py-2">
                              <div className="text-lg font-bold text-white">
                                {ds.total_size_mb >= 1024 ? `${(ds.total_size_mb / 1024).toFixed(1)} GB` : `${ds.total_size_mb} MB`}
                              </div>
                              <div className="text-[10px] text-gray-500">Size</div>
                            </div>
                            <div className="bg-gray-800/50 rounded-lg px-3 py-2">
                              <div className="text-lg font-bold text-white">{Object.keys(ds.files).length}</div>
                              <div className="text-[10px] text-gray-500">Files</div>
                            </div>
                          </div>

                          {/* Timeframe pills */}
                          <div className="flex flex-wrap gap-1.5 mb-4">
                            {ds.timeframes.map(tf => (
                              <span key={tf} className="px-2 py-0.5 text-[10px] font-mono bg-gray-800 text-gray-400 rounded border border-gray-700">
                                {tf}
                              </span>
                            ))}
                          </div>

                          {/* Per-file breakdown */}
                          {Object.keys(ds.files).length > 0 && (
                            <div className="border-t border-gray-800 pt-3">
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                {Object.entries(ds.files).map(([label, file]) => (
                                  <div key={label} className="flex items-center justify-between text-xs">
                                    <span className="text-gray-400 font-mono">{file.name}</span>
                                    <span className="text-gray-500">
                                      {file.size_mb >= 1024 ? `${(file.size_mb / 1024).toFixed(1)} GB` : `${file.size_mb} MB`}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Mount path */}
                          <div className="mt-3 flex items-center gap-1.5">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                            <code className="text-[10px] text-gray-500">{ds.mount_path}</code>
                          </div>

                          {/* Pipeline — clickable expand/collapse */}
                          <div className="mt-3 border-t border-gray-800 pt-3">
                            <button
                              onClick={() => togglePipeline(ds.id)}
                              className="w-full flex items-center justify-between text-xs hover:bg-gray-800/40 -mx-1 px-1 py-1 rounded transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] text-gray-600">{isExpanded ? '\u25BC' : '\u25B6'}</span>
                                <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Pipeline</span>
                                <div className={`w-1.5 h-1.5 rounded-full ${sync.dotColor}`} />
                                <span className={`${sync.color}`}>{sync.label}</span>
                                {lastRun?.started_at && (
                                  <span className="text-gray-600">
                                    {parseUTC(lastRun.started_at).toLocaleString(undefined, {
                                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                                    })}
                                  </span>
                                )}
                              </div>
                              {lastRun && (
                                <span className="text-gray-600 font-mono">{formatCost(lastRun.total_cost)}</span>
                              )}
                            </button>

                            {isExpanded && (
                              <div className="mt-2 space-y-2 text-[11px]">
                                {/* Source */}
                                <div className="flex gap-3">
                                  <span className="text-gray-600 w-24 shrink-0">Source</span>
                                  <span className="text-gray-300">{pipeline.source}</span>
                                </div>

                                {/* Refresh */}
                                <div className="flex gap-3">
                                  <span className="text-gray-600 w-24 shrink-0">Refresh</span>
                                  <div>
                                    <code className="text-gray-400">{pipeline.refreshScript}</code>
                                    <div className="text-gray-600 mt-0.5">{pipeline.refreshSchedule}</div>
                                    {ds.last_refresh && (
                                      <div className="text-gray-600 mt-0.5">
                                        Last: {parseUTC(ds.last_refresh).toLocaleString(undefined, {
                                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Replication */}
                                <div className="flex gap-3">
                                  <span className="text-gray-600 w-24 shrink-0">Replication</span>
                                  <div>
                                    <div className="text-gray-400">{pipeline.replicationSchedule}</div>
                                    {lastRun && (
                                      <div className="text-gray-600 mt-0.5 font-mono">
                                        {lastRun.files_transferred}/{lastRun.files_scanned} files
                                        {lastRun.files_skipped > 0 && ` (${lastRun.files_skipped} cached)`}
                                        {' \u00B7 '}{formatDuration(lastRun.duration_seconds)}
                                        {lastRun.avg_throughput_mbps ? ` \u00B7 ${lastRun.avg_throughput_mbps} MB/s` : ''}
                                        {' \u00B7 '}{formatCost(lastRun.total_cost)}
                                      </div>
                                    )}
                                    {lastRun?.error_message && (
                                      <div className="text-red-400 mt-0.5">{lastRun.error_message}</div>
                                    )}
                                  </div>
                                </div>

                                {/* S3 Path */}
                                <div className="flex gap-3">
                                  <span className="text-gray-600 w-24 shrink-0">S3 Path</span>
                                  <code className="text-gray-500">{pipeline.s3Prefix}</code>
                                </div>

                                {/* Delivery */}
                                <div className="flex gap-3">
                                  <span className="text-gray-600 w-24 shrink-0">Delivery</span>
                                  <div className="text-gray-400">
                                    <div>AWS: {pipeline.delivery.aws}</div>
                                    <div>GCP: {pipeline.delivery.gcp}</div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {datasets.length === 0 && !statsLoading && (
                  <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center text-sm text-gray-500">
                    No datasets found. Run the refresh pipeline to populate.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'cost' && (
          <div className="space-y-6">
            {costLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 border-2 border-gray-700 border-t-white rounded-full animate-spin" />
              </div>
            ) : costSummary ? (
              <>
                {/* Cost summary cards */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                    <div className="text-2xl font-bold text-white">{formatCost(costSummary.total_cost)}</div>
                    <div className="text-xs text-gray-500 mt-1">30-Day Total Cost</div>
                  </div>
                  <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                    <div className="text-lg font-semibold text-white">
                      {formatCost(costSummary.total_egress_cost)}
                      <span className="text-xs text-gray-500 font-normal ml-1">egress</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {formatCost(costSummary.total_storage_cost)} storage / {formatCost(costSummary.total_api_cost)} API
                    </div>
                  </div>
                  <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                    <div className="text-2xl font-bold text-white">{costSummary.total_runs}</div>
                    <div className="text-xs text-gray-500 mt-1">Replication Runs</div>
                  </div>
                  <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                    <div className="text-lg font-semibold text-white">
                      {costSummary.avg_throughput_mbps > 0 ? `${costSummary.avg_throughput_mbps} MB/s` : '-'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Avg Throughput</div>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="px-1.5 py-0.5 text-[10px] bg-green-500/10 text-green-400 rounded border border-green-500/20">32MB chunks</span>
                    </div>
                  </div>
                </div>

                {/* Cost trend chart */}
                {costSummary.cost_trend.length > 0 && (
                  <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-gray-300 mb-4">Daily Replication Cost</h3>
                    <div className="flex items-end gap-1 h-32">
                      {costSummary.cost_trend.map((d, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <div
                            className="w-full bg-blue-500/30 border border-blue-500/40 rounded-t min-h-[2px] transition-all"
                            style={{ height: `${Math.max((d.cost / maxCost) * 100, 2)}%` }}
                            title={`${d.date}: ${formatCost(d.cost)} (${formatBytes(d.bytes)})`}
                          />
                          {i % Math.max(1, Math.floor(costSummary.cost_trend.length / 7)) === 0 && (
                            <span className="text-[9px] text-gray-600 -rotate-45 origin-top-left whitespace-nowrap">
                              {d.date.slice(5)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Per-run table */}
                <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-800">
                    <h3 className="text-sm font-semibold text-gray-300">Replication Runs</h3>
                  </div>
                  {runsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-5 h-5 border-2 border-gray-700 border-t-white rounded-full animate-spin" />
                    </div>
                  ) : runs.length === 0 ? (
                    <div className="px-5 py-8 text-center text-sm text-gray-500">
                      No replication runs recorded yet
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-gray-500 uppercase">
                            <th className="px-4 py-2 text-left font-medium">Date</th>
                            <th className="px-4 py-2 text-left font-medium">Dataset</th>
                            <th className="px-4 py-2 text-left font-medium">Trigger</th>
                            <th className="px-4 py-2 text-left font-medium">Status</th>
                            <th className="px-4 py-2 text-right font-medium">Files</th>
                            <th className="px-4 py-2 text-right font-medium">Transferred</th>
                            <th className="px-4 py-2 text-right font-medium">Duration</th>
                            <th className="px-4 py-2 text-right font-medium">Throughput</th>
                            <th className="px-4 py-2 text-right font-medium">Cost</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                          {runs.map(run => (
                            <tr key={run.id} className="text-gray-300 hover:bg-gray-800/50">
                              <td className="px-4 py-2.5 whitespace-nowrap">
                                {run.started_at ? parseUTC(run.started_at).toLocaleString(undefined, {
                                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                }) : '-'}
                              </td>
                              <td className="px-4 py-2.5">
                                <span className="text-xs text-gray-400 font-mono">
                                  {run.dataset_id || 'all'}
                                </span>
                              </td>
                              <td className="px-4 py-2.5">
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                  run.trigger === 'admin_manual' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' :
                                  run.trigger === 'event_driven' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' :
                                  'bg-gray-800 text-gray-400 border border-gray-700'
                                }`}>
                                  {run.trigger}
                                </span>
                              </td>
                              <td className="px-4 py-2.5">
                                <span className={`text-xs ${
                                  run.status === 'completed' ? 'text-green-400' :
                                  run.status === 'failed' ? 'text-red-400' : 'text-yellow-400'
                                }`}>
                                  {run.status}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-xs">
                                {run.files_transferred}/{run.files_scanned}
                                {run.files_skipped > 0 && (
                                  <span className="text-gray-600 ml-1">({run.files_skipped} skip)</span>
                                )}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-xs">
                                {formatBytes(run.bytes_transferred)}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-xs">
                                {formatDuration(run.duration_seconds)}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-xs">
                                {run.avg_throughput_mbps ? `${run.avg_throughput_mbps} MB/s` : '-'}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-xs">
                                {formatCost(run.total_cost)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-gray-500 text-sm">No cost data available</div>
            )}
          </div>
        )}

        {activeTab === 'browse' && (
          <div className="space-y-4">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1 text-sm">
              <button
                onClick={() => { setBrowsePath(''); fetchBrowse(''); }}
                className="text-blue-400 hover:text-blue-300"
              >
                datasets/
              </button>
              {browsePath && browsePath.split('/').filter(Boolean).map((part, i, arr) => {
                const fullPath = arr.slice(0, i + 1).join('/');
                return (
                  <span key={i} className="flex items-center gap-1">
                    <span className="text-gray-600">/</span>
                    <button
                      onClick={() => { setBrowsePath(fullPath); fetchBrowse(fullPath); }}
                      className="text-blue-400 hover:text-blue-300"
                    >
                      {part}
                    </button>
                  </span>
                );
              })}
            </div>

            {/* File listing */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
              {browseLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-5 h-5 border-2 border-gray-700 border-t-white rounded-full animate-spin" />
                </div>
              ) : browseItems.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-gray-500">
                  No files found at this path
                </div>
              ) : (
                <div className="divide-y divide-gray-800">
                  {browseItems.map((item, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm ${
                        item.type === 'directory' ? 'hover:bg-gray-800/50 cursor-pointer' : ''
                      }`}
                      onClick={() => {
                        if (item.type === 'directory') {
                          const newPath = item.path.replace(/^datasets\/?/, '').replace(/\/$/, '');
                          setBrowsePath(newPath);
                          fetchBrowse(newPath);
                        }
                      }}
                    >
                      {item.type === 'directory' ? (
                        <svg className="w-4 h-4 text-yellow-500 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      )}
                      <span className={`flex-1 ${item.type === 'directory' ? 'text-blue-400' : 'text-gray-300'}`}>
                        {item.name}
                      </span>
                      {item.size !== undefined && (
                        <span className="text-xs text-gray-500 font-mono">{formatBytes(item.size)}</span>
                      )}
                      {item.last_modified && (
                        <span className="text-xs text-gray-600">
                          {parseUTC(item.last_modified).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
